import {
  Engine as BabylonEngine,
  Scene,
  ArcRotateCamera,
  Vector3,
  HemisphericLight,
  DirectionalLight,
  Color3,
  Color4,
  MeshBuilder,
  StandardMaterial,
} from '@babylonjs/core';

export interface EngineConfig {
  canvasId: string;
  antialias?: boolean;
  adaptToDeviceRatio?: boolean;
}

export class Engine {
  private canvas: HTMLCanvasElement;
  private babylonEngine: BabylonEngine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private isRunning: boolean = false;

  private onLoadProgress?: (progress: number, message: string) => void;

  constructor(config: EngineConfig) {
    const canvas = document.getElementById(config.canvasId) as HTMLCanvasElement;
    if (!canvas) {
      throw new Error(`Canvas with id "${config.canvasId}" not found`);
    }
    this.canvas = canvas;

    this.babylonEngine = new BabylonEngine(
      this.canvas,
      config.antialias ?? true,
      undefined,
      config.adaptToDeviceRatio ?? true
    );

    this.scene = new Scene(this.babylonEngine);
    this.scene.clearColor = new Color4(0.529, 0.808, 0.922, 1); // Sky blue

    this.camera = this.createCamera();
    this.setupLighting();
    this.setupResizeHandler();
  }

  public setLoadProgressCallback(callback: (progress: number, message: string) => void): void {
    this.onLoadProgress = callback;
  }

  public updateProgress(progress: number, message: string): void {
    if (this.onLoadProgress) {
      this.onLoadProgress(progress, message);
    }
  }

  private createCamera(): ArcRotateCamera {
    const camera = new ArcRotateCamera(
      'MainCamera',
      -Math.PI / 4,  // Alpha - horizontal rotation
      Math.PI / 3,   // Beta - vertical rotation
      50,            // Radius - distance from target
      new Vector3(8, 10, 8),
      this.scene
    );

    camera.lowerRadiusLimit = 5;
    camera.upperRadiusLimit = 200;
    camera.lowerBetaLimit = 0.1;
    camera.upperBetaLimit = Math.PI / 2 - 0.05;

    camera.wheelPrecision = 20;
    camera.panningSensibility = 50;
    camera.panningInertia = 0.5;

    camera.attachControl(this.canvas, true);

    return camera;
  }

  public setCameraTarget(target: Vector3): void {
    this.camera.setTarget(target);
  }

  private setupLighting(): void {
    // Ambient light (hemisphere)
    const hemisphericLight = new HemisphericLight(
      'HemisphericLight',
      new Vector3(0, 1, 0),
      this.scene
    );
    hemisphericLight.intensity = 0.7;
    hemisphericLight.groundColor = new Color3(0.4, 0.4, 0.5);

    // Sun (directional light)
    const sunLight = new DirectionalLight(
      'SunLight',
      new Vector3(-0.5, -1, -0.5),
      this.scene
    );
    sunLight.intensity = 0.9;
    sunLight.diffuse = new Color3(1, 0.98, 0.9);
  }

  public async initialize(): Promise<void> {
    this.updateProgress(10, 'Inicializando engine...');

    await this.createSkybox();
    this.updateProgress(30, 'Skybox criado...');

    this.updateProgress(50, 'Preparando mundo...');
  }

  private async createSkybox(): Promise<void> {
    const skybox = MeshBuilder.CreateBox('Skybox', { size: 1000 }, this.scene);
    const skyboxMaterial = new StandardMaterial('SkyboxMaterial', this.scene);

    skyboxMaterial.backFaceCulling = false;
    skyboxMaterial.disableLighting = true;

    // Sky gradient color
    skyboxMaterial.emissiveColor = new Color3(0.529, 0.808, 0.922);

    skybox.material = skyboxMaterial;
    skybox.infiniteDistance = true;
  }

  public start(): void {
    if (this.isRunning) return;

    this.isRunning = true;

    this.babylonEngine.runRenderLoop(() => {
      this.scene.render();
    });
  }

  public stop(): void {
    this.isRunning = false;
    this.babylonEngine.stopRenderLoop();
  }

  private setupResizeHandler(): void {
    window.addEventListener('resize', () => {
      this.babylonEngine.resize();
    });
  }

  public getScene(): Scene {
    return this.scene;
  }

  public getCamera(): ArcRotateCamera {
    return this.camera;
  }

  public getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  public getBabylonEngine(): BabylonEngine {
    return this.babylonEngine;
  }

  public dispose(): void {
    this.stop();
    this.scene.dispose();
    this.babylonEngine.dispose();
  }
}
